import { useQuery } from '@tanstack/react-query';
import { api, ResponseMap } from '@/lib/utils/api.utils';
import { ERROR_CODE } from '@/lib/config/errorCode';
import { useSession } from '@/lib/hooks/useSession';
import { SERVER_CODE } from '@/lib/config/serverCode';
import { toaster } from '@/components/ui/toaster';
const requestUrl = process.env.NEXT_PUBLIC_REQUEST_URL;

interface ChainGasType {
  chainId: string;
  gasType: number;
  gasTypePromptInfo: string;
}

interface UserWallet {
  chainId: number;
  isUsing: number;
  tokenValue: number;
  transferInRemark: string;
  txAmount: string;
  userId: number;
  userWalletAddress: string;
  walletGroupId: number;
  walletId: number;
  walletTag: string;
}

interface WalletGroup {
  isUsing: number;
  userWallets: UserWallet[];
  wallGroupValues: number;
  walletGroupId: number;
  chainId: number;
}

interface UserAccountResponse {
  gasType: number;
  gasTypeBase: number;
  gasTypeBsc: number;
  gasTypeSol: number;
  gasTypeTrx: number;
  isAntiMev: boolean;
  isAutoSlippage: boolean;
  isFastTrade: number;
  slippage: number;
  totalChainGasType: ChainGasType[];
  totalValue: number;
  userWalletGroups: WalletGroup[];
}

export function useUserAccount() {
  const { data: sessionData } = useSession();
  return useQuery<UserAccountResponse, Error>({
    queryKey: ['userAccount'],
    queryFn: () => requestUserAccount(),
    enabled: !!sessionData?.isLogin,
  });
}

export async function requestUserAccount(): Promise<UserAccountResponse> {
  try {
    const response = await api.get(
      `${requestUrl}/v1/account/user_currency_list`,
    );

    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR);
    }

    const data = await response.json<ResponseMap<UserAccountResponse>>();

    if (!data.data) {
      throw new Error(ERROR_CODE.EMPTY_DATA);
    }

    return data.data;
  } catch (error) {
    console.error('Error requestUserAccount:', error);
    throw error;
  }
}

export type tradeAccountProps = {
  is_anti_mev: boolean;
  is_auto_slippage: boolean;
  slippage: string;
  gas_type_sol: string | number;
};

export async function requestSetTradeAccount(
  params: tradeAccountProps,
): Promise<{ code: number; message: string }> {
  const response = await api.post(`${requestUrl}/v1/account/trade_setting`, {
    json: params,
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data =
    await response.json<ResponseMap<{ code: number; message: string }>>();
  return { code: data.code, message: data.message };
}

export interface TokenTransferBody {
  email_code: string;
  code: string;
  wallet_group_id: number;
  chain_id: number;
  balance: string;
  recipient_address: string;
  token_address: string;
}

export interface TokenTransferResponse {
  data: {
    tx_hash: string;
  };
}

export async function requestTokenTransfer(
  params: TokenTransferBody,
): Promise<TokenTransferResponse | undefined> {
  const response = await api.post(`${requestUrl}/v1/account/token_transfer`, {
    json: params,
  });
  if (!response.ok) {
    throw new Error(ERROR_CODE.NETWORK_ERROR);
  }
  const data = await response.json<ResponseMap<TokenTransferResponse>>();

  if (data.code !== SERVER_CODE.SUCCESS) {
    throw new Error(data.message);
  }

  return data.data;
}
